/* ── PageViewer — zoomable page image display ────────────────
   Shows a single PDF page image fetched from R2.
   • Click-to-zoom on desktop (toggle fit-width ↔ full-size)
   • Ctrl+wheel (or scroll when zoomed) to zoom in/out
   • Pinch-to-zoom on mobile (CSS transform + touch events)
   • Fullscreen button
   • Loading skeleton + graceful error state
   ─────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  /** Image URL (from getPageImageUrl) */
  src: string;
  /** Accessible alt text */
  alt: string;
  /** Page number label to show in badge */
  pageNumber: number;
  /** Called when image fails to load */
  onError?: () => void;
}

export default function PageViewer({ src, alt, pageNumber, onError }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  /* ── Fullscreen listener ───────────────────────────────────── */
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  /* ── Pinch-to-zoom state ─────────────────────────────────── */
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const pinchRef = useRef<{
    startDist: number;
    startScale: number;
    startMid: { x: number; y: number };
    startTranslate: { x: number; y: number };
  } | null>(null);

  /* Reset zoom state + set image load timeout when src changes */
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setLoaded(false);
    setErrored(false);
    setZoomed(false);
    setScale(1);
    setTranslate({ x: 0, y: 0 });

    // Don't let the skeleton spin forever — 8s max
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    loadTimeoutRef.current = setTimeout(() => {
      setErrored(true);
      setLoaded(false);
      onError?.();
    }, 8000);

    return () => {
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    };
  }, [src, onError]);

  /* ── Desktop click-to-zoom toggle ───────────────────────── */
  const handleClick = useCallback(() => {
    if (errored || !loaded) return;
    // Don't toggle zoom if user was pinch-zooming
    if (scale !== 1) {
      // Reset to fit-width
      setScale(1);
      setTranslate({ x: 0, y: 0 });
      setZoomed(false);
      return;
    }
    setZoomed((z) => !z);
  }, [errored, loaded, scale]);

  /* ── Ctrl+wheel / wheel-when-zoomed zoom ─────────────────── */
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      // Only intercept when Ctrl/Meta held OR when already in a scaled state
      if (!e.ctrlKey && !e.metaKey && scale === 1 && !zoomed) return;
      e.preventDefault();
      const delta = -e.deltaY * 0.003;
      setScale((prev) => {
        const next = Math.min(Math.max(prev + delta * prev, 1), 6);
        if (next <= 1) {
          setTranslate({ x: 0, y: 0 });
          setZoomed(false);
        } else {
          setZoomed(false); // use scale path, not click-toggle path
        }
        return next <= 1 ? 1 : next;
      });
    },
    [scale, zoomed],
  );

  /* ── Touch handlers for pinch-to-zoom ────────────────────── */
  const getDistance = (t1: React.Touch, t2: React.Touch) =>
    Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const getMidpoint = (t1: React.Touch, t2: React.Touch) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const dist = getDistance(e.touches[0], e.touches[1]);
        const mid = getMidpoint(e.touches[0], e.touches[1]);
        pinchRef.current = {
          startDist: dist,
          startScale: scale,
          startMid: mid,
          startTranslate: { ...translate },
        };
      }
    },
    [scale, translate],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const dist = getDistance(e.touches[0], e.touches[1]);
        const mid = getMidpoint(e.touches[0], e.touches[1]);
        const ratio = dist / pinchRef.current.startDist;
        const newScale = Math.min(Math.max(pinchRef.current.startScale * ratio, 1), 5);

        const dx = mid.x - pinchRef.current.startMid.x;
        const dy = mid.y - pinchRef.current.startMid.y;

        setScale(newScale);
        setTranslate({
          x: pinchRef.current.startTranslate.x + dx,
          y: pinchRef.current.startTranslate.y + dy,
        });
      }
    },
    [],
  );

  const handleTouchEnd = useCallback(() => {
    pinchRef.current = null;
    // Snap back to 1 if barely zoomed
    if (scale < 1.1) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
    }
  }, [scale]);

  /* ── Image load / error ──────────────────────────────────── */
  const handleLoad = useCallback(() => {
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    setLoaded(true);
  }, []);
  const handleError = useCallback(() => {
    setErrored(true);
    setLoaded(false);
    onError?.();
  }, [onError]);

  /* ── Compute transform ──────────────────────────────────── */
  const isTransformed = zoomed || scale !== 1;
  const imgStyle: React.CSSProperties = {};
  if (scale !== 1) {
    imgStyle.transform = `translate(${translate.x}px, ${translate.y}px) scale(${scale})`;
    imgStyle.transformOrigin = 'center center';
    imgStyle.cursor = 'grab';
  } else if (zoomed) {
    // Full-size: natural width, user scrolls container
    imgStyle.width = 'auto';
    imgStyle.maxWidth = 'none';
    imgStyle.cursor = 'zoom-out';
  } else {
    imgStyle.cursor = 'zoom-in';
  }

  if (errored) {
    return (
      <div className="page-viewer-container">
        <div className="page-image-unavailable page-image-offline-notice">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>Page images temporarily unavailable — try again shortly</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`page-viewer-container${isTransformed ? ' page-viewer-container--zoomed' : ''}`}
      onClick={handleClick}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Loading skeleton — shown until image loads */}
      {!loaded && (
        <div className="page-image-skeleton" aria-label="Loading page image" />
      )}

      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`page-viewer-img${zoomed ? ' page-viewer-img--zoomed' : ''}`}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        style={{
          display: loaded ? 'block' : 'none',
          ...imgStyle,
        }}
        draggable={false}
      />

      {/* Page number badge */}
      {loaded && (
        <span className="page-image-label" aria-hidden="true">
          p.{pageNumber}
        </span>
      )}

      {/* Fullscreen button */}
      {loaded && (
        <button
          className="page-viewer-fullscreen-btn"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          title={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
        >
          {isFullscreen ? (
            /* compress icon */
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
              <line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/>
            </svg>
          ) : (
            /* expand icon */
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
              <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
            </svg>
          )}
        </button>
      )}

      {/* Zoom hint (desktop only, shown on first view) */}
      {loaded && !zoomed && scale === 1 && (
        <span className="page-viewer-zoom-hint" aria-hidden="true">
          Click to zoom · Ctrl+scroll
        </span>
      )}
    </div>
  );
}
